/* eslint-disable react-native/no-inline-styles */
import React, { memo, useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';

import { ItemType } from '@interfaces/search';

import { font as initialFont } from '@styles/fonts';
import { scale as initialScale } from '@components/frame';
import { getBoxShadowStyle } from '@styles/shadow';
import { SmallLoading } from '@components/loading';
import Svg from '@styles/icons/svg';

import TouchableElement from '@components/common/touchableElement';
import Image from '@components/common/Image';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { SimpleOnPress } from '@interfaces/base';
import { ColorFunction } from '@styles/colors/types';
import { Document } from '@api/domain';

const LineMultimediaButton = memo(
  ({
    C,
    onPress,
    generateImageURL,
    documentData,
    onAdditionalBtnPress,
    height = 42,
    loading,
    borderBottom = false,
    shadow = true,
    borderRadiusTop = true,
    borderRadiusBottom = true,
    bg = 'Background / Light / Field',
    imgBg = 'Overlay/Light/Dim light',
    fontTitle: customFontTitle,
    fontText: customFontText,
    title,
    text,
    additionalText,
    additionalBtnIcon,
    additionalBtnColor,
    highlighted,
    scale = initialScale,
    font = initialFont,
  }: Partial<SimpleOnPress> & {
    C: ColorFunction;
    generateImageURL: any;
    documentData?: Document | null;
    onAdditionalBtnPress?: () => void;
    height?: number;
    loading?: boolean;
    borderBottom?: boolean;
    shadow?: boolean;
    borderRadiusTop?: boolean;
    borderRadiusBottom?: boolean;
    bg?: string;
    imgBg?: string;
    fontTitle?: string;
    fontText?: string;
    title?: string | null;
    text?: string | null;
    additionalText?: string | null;
    additionalBtnIcon?: string;
    highlighted?: string;
    scale?: ScaleFunction;
    font?: typeof initialFont;
  }) => {
    const [imageLink, setImageLink] = useState(null);

    const TAG_NAMING = {
      Part: 'Part',
      ProductionOrder: 'Production Order',
      Recipe: 'Recipe',
    };

    useEffect(() => {
      (async () => {
        if (documentData?.contentHash) {
          const imageLink = await generateImageURL({
            size: '_1x',
            cropped: documentData.isCropped,
            item: {
              imageHash: documentData.contentHash,
              ...documentData,
            },
            page: documentData.pageNumber,
          });
          setImageLink(imageLink);
        }
      })();
    }, [documentData?.contentHash]);

    const splitTitle = (highlightedText: string, itemDescription: string) => {
      const index = itemDescription.indexOf(highlightedText);

      if (index === -1) {
        return {
          before: itemDescription,
          highlighted: '',
          after: '',
        };
      }

      const before = itemDescription.substring(0, index);
      const highlighted = highlightedText;
      const after = itemDescription.substring(index + highlightedText.length);

      return { before, highlighted, after };
    };

    const renderTitle = () => {
      if (highlighted) {
        const parts = splitTitle(highlighted, title || '');
        return (
          <Text numberOfLines={1} style={[font[customFontTitle || 'Subhead 1'], { color: C('Label/Light/Primary'), height: scale(22) }]}>
            <Text>{parts.before}</Text>
            <Text numberOfLines={1} style={[font['Headline'], { color: C('Label/Light/Primary') }]}>
              <Text>{parts.highlighted}</Text>
            </Text>
            <Text>{parts.after}</Text>
          </Text>
        );
      }
      return (
        <Text numberOfLines={1} style={[font[customFontTitle || 'Headline'], { color: C('Label/Light/Primary'), height: scale(22) }]}>
          {title}
        </Text>
      );
    };
    return (
      <View
        style={[
          { height: scale(height), userSelect: 'none' },
          shadow && {
            borderRadius: scale(SCREEN_BORDER_RADIUS),
          },
          shadow && getBoxShadowStyle({ type: 'Field', C }),
          borderBottom && {
            borderBottomColor: C('Hairline/Light/No trans'),
            borderBottomWidth: StyleSheet.hairlineWidth,
          },
        ]}
      >
        <TouchableElement onPress={onPress} hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}>
          {({ boxStyle, pressHoverColor }) => (
            <View
              style={[
                {
                  backgroundColor: C(pressHoverColor(bg)),
                  height: '100%',
                  width: '100%',
                  justifyContent: 'center',
                  alignContent: 'center',
                },
                borderRadiusTop && {
                  borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
                  borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
                },
                borderRadiusBottom && {
                  borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
                  borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
                },
                boxStyle,
              ]}
            >
              {loading ? (
                <SmallLoading />
              ) : (
                <View style={{ flexDirection: 'row', paddingHorizontal: scale(15) }}>
                  {imageLink ? (
                    <Image
                      style={[
                        {
                          width: scale(73),
                          height: scale(44),
                          marginVertical: scale(8),
                          marginRight: scale(10),
                        },
                      ]}
                      source={{ uri: imageLink }}
                    />
                  ) : (
                    <View
                      style={{
                        width: scale(73),
                        marginVertical: scale(8),
                        marginRight: scale(10),
                        backgroundColor: C(imgBg),
                      }}
                    />
                  )}
                  <View style={{ paddingVertical: scale(8), flex: 1 }}>
                    {renderTitle()}
                    <Text numberOfLines={1} style={[font[customFontText || 'Subhead 1'], { color: C('Label/Light/Secondary'), height: scale(22) }]}>
                      {text}
                    </Text>
                  </View>
                  <View
                    style={{
                      paddingBottom: scale(additionalText ? 8 : 0),
                      alignItems: 'flex-end',
                      alignSelf: 'flex-end',
                      height: '100%',
                      justifyContent: 'space-around',
                    }}
                  >
                    {additionalText && (
                      <Text
                        numberOfLines={1}
                        style={[font[customFontText || 'Caption 2'], { color: C('Label/Light/Tertiary'), textAlign: 'center' }]}
                      >
                        {TAG_NAMING[additionalText as ItemType] || additionalText}
                      </Text>
                    )}
                    {onAdditionalBtnPress && additionalBtnIcon && (
                      <TouchableElement onPress={onAdditionalBtnPress}>
                        {({ boxStyle }) => (
                          <View style={[boxStyle]}>
                            <Svg icon={additionalBtnIcon} color={additionalBtnColor} height={16} width={16} />
                          </View>
                        )}
                      </TouchableElement>
                    )}
                  </View>
                </View>
              )}
            </View>
          )}
        </TouchableElement>
      </View>
    );
  },
  (prevProps, nextProps) =>
    prevProps.documentData?.contentHash === nextProps.documentData?.contentHash &&
    prevProps.title === nextProps.title &&
    prevProps.borderBottom === nextProps.borderBottom &&
    prevProps.borderRadiusBottom === nextProps.borderRadiusBottom &&
    prevProps.borderRadiusTop === nextProps.borderRadiusTop
);

export default LineMultimediaButton;
